import { c as _c } from "react/compiler-runtime";
import type { Route } from "next";
import Image from "next/image";
import Link from "next/link";
import { HoldingShape, type HoldingShapeVariant } from "@repo/ui/holding-shape";

/** Vibe styling keyed by journey sub-category. */
export const VIBE: Record<string, {
  label: string;
  dot: string;
  bg: string;
}> = {
  heritage: {
    label: "Heritage",
    dot: "#D4A95C",
    bg: "#F8E9C8"
  },
  food: {
    label: "Food trail",
    dot: "#FF946A",
    bg: "#FFE0CC"
  },
  art: {
    label: "Art trail",
    dot: "#5DA8E8",
    bg: "#B8DBF7"
  },
  family: {
    label: "Family",
    dot: "#68C187",
    bg: "#CCE88A"
  },
  active: {
    label: "Active",
    dot: "#FE5445",
    bg: "#FFD7BC"
  }
};
export const CARD_SHAPES: HoldingShapeVariant[] = ["arch", "squircle", "round-rectangle-stack"];

/**
 * Single source of truth for a journey's overall duration label, shared by
 * the grid card and the detail hero so they never disagree. Buckets long
 * days to "Half day" / "Full day", otherwise an approximate hour count.
 */
export function journeyDurationLabel(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return "Half day";
  if (minutes >= 6 * 60) return "Full day";
  if (minutes >= 4 * 60) return "Half day";
  const hours = Math.round(minutes / 60);
  return `~${hours} hr${hours === 1 ? "" : "s"}`;
}
export interface JourneyCardData {
  _id: string;
  title: string;
  slug: string;
  subCategory: string | null;
  description: string | null;
  imageUrl: string | null;
  stopCount: number;
  duration: string;
  districts: number;
}

/**
 * One journey in a grid — the shared card used by both the planner's
 * journeys grid and the "more journeys" section on a detail page.
 */
export function JourneyCard(t0) {
  const $ = _c(38);
  const {
    journey,
    locale,
    shapeIndex: t1
  } = t0;
  const shapeIndex = t1 === undefined ? 0 : t1;
  const vibe = journey.subCategory ? VIBE[journey.subCategory] : undefined;
  const shape = CARD_SHAPES[shapeIndex % CARD_SHAPES.length];
  const t2 = `/${locale}${journey.slug}` as Route;
  let t3;
  if ($[0] !== journey.imageUrl || $[1] !== journey.title) {
    t3 = journey.imageUrl ? <Image src={journey.imageUrl} alt={journey.title} fill={true} sizes="(min-width: 1024px) 420px, (min-width: 640px) 50vw, 100vw" className="object-cover transition-transform duration-700 group-hover:scale-105" /> : null;
    $[0] = journey.imageUrl;
    $[1] = journey.title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <div className="relative h-full w-full bg-deep-sea-blue-500/8">{t3}</div>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== shape || $[6] !== t4) {
    t5 = <HoldingShape variant={shape} className="w-full">{t4}</HoldingShape>;
    $[5] = shape;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== vibe) {
    t6 = vibe ? <span className="absolute bottom-[8.33%] left-1/2 z-10 inline-flex -translate-x-1/2 translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-white px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-deep-sea-blue-500 shadow-md ring-1 ring-deep-sea-blue-500/5"><span className="block h-1.5 w-1.5 rounded-full" style={{
        background: vibe.dot
      }} />{vibe.label}</span> : null;
    $[8] = vibe;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div className="relative">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== journey.title) {
    t8 = <h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-deep-sea-blue-500 transition-colors group-hover:text-stone-700">{journey.title}</h3>;
    $[13] = journey.title;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== journey.description) {
    t9 = journey.description ? <p className="typography-body-sm-regular text-deep-sea-blue-500/65">{journey.description}</p> : null;
    $[15] = journey.description;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <dt className="opacity-70">Duration</dt>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== journey.duration) {
    t11 = <div>{t10}<dd className="mt-1 text-sm font-normal normal-case tracking-normal text-deep-sea-blue-500">{journey.duration}</dd></div>;
    $[18] = journey.duration;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <dt className="opacity-70">Stops</dt>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== journey.stopCount) {
    t13 = <div>{t12}<dd className="mt-1 text-sm font-normal normal-case tracking-normal text-deep-sea-blue-500">{journey.stopCount}</dd></div>;
    $[21] = journey.stopCount;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <dt className="opacity-70">Areas</dt>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = journey.districts || "\u2014";
  let t16;
  if ($[24] !== t15) {
    t16 = <div>{t14}<dd className="mt-1 text-sm font-normal normal-case tracking-normal text-deep-sea-blue-500">{t15}</dd></div>;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t11 || $[27] !== t13 || $[28] !== t16) {
    t17 = <dl className="mt-2 grid grid-cols-3 gap-3 border-t border-deep-sea-blue-500/10 pt-4 text-[10px] font-semibold uppercase tracking-[0.15em] text-deep-sea-blue-500/55">{t11}{t13}{t16}</dl>;
    $[26] = t11;
    $[27] = t13;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17 || $[31] !== t8 || $[32] !== t9) {
    t18 = <div className="flex flex-1 flex-col gap-3 px-6 pt-10">{t8}{t9}{t17}</div>;
    $[30] = t17;
    $[31] = t8;
    $[32] = t9;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18 || $[35] !== t2 || $[36] !== t7) {
    t19 = <Link href={t2} className="group flex h-full flex-col" data-testid="journey-card">{t7}{t18}</Link>;
    $[34] = t18;
    $[35] = t2;
    $[36] = t7;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  return t19;
}